/**
 * Dev-log SQLite sink — the import-safe slice of app-common/logging.ts.
 *
 * Lives in its own module (no jQuery, no SignalR, no module-scope side
 * effects) so framework-agnostic code and anything in the jasmine/ts-node
 * spec import graph can use recordDevLog without dragging in logging.ts,
 * whose module-scope `$(...)` bootstrap throws outside a browser. That
 * throw is deliberate and useful — don't re-import logging.ts from spec-
 * reachable code; import from here instead.
 */

/** Whether to mirror logs to the DevServer's SQLite sink (local dev only). */
export let logToDevSqlite = false;

if (typeof (__gameplan_is_local_development) !== 'undefined' && __gameplan_is_local_development) {
    logToDevSqlite = true;
}

// taken from http://stackoverflow.com/questions/17415579/how-to-iso-8601-format-a-date-with-timezone-offset-in-javascript
function formatLocalDate(when: Date): string {
    let tzo = -when.getTimezoneOffset(),
        dif = tzo >= 0 ? '+' : '-',
        pad = (num: number) => {
            const norm = Math.abs(Math.floor(num));
            return (norm < 10 ? '0' : '') + norm;
        };
    return when.getFullYear()
        + '-' + pad(when.getMonth() + 1)
        + '-' + pad(when.getDate())
        + 'T' + pad(when.getHours())
        + ':' + pad(when.getMinutes())
        + ':' + pad(when.getSeconds())
        + dif + pad(tzo / 60)
        + ':' + pad(tzo % 60);
}

export function formatDate(when: Date): string {
    return formatLocalDate(when);
}

/**
 * Direct entry-point into the dev-log SQLite sink. Skips the batched
 * __gpLogs queue — intended for low-frequency, high-value events like
 * "scenario refreshed from server" or "browser sent turn body" where
 * the caller already has structured payload and (optionally) scenario
 * before/after/diff state.
 *
 * Only active when __gameplan_is_local_development is truthy; in prod
 * it's a no-op so we never accidentally ship plan state to a logging
 * backend.
 *
 * Fire-and-forget. Failures are swallowed.
 */
export interface DevLogEntry {
    category: string;
    level?: 'debug' | 'info' | 'warn' | 'error';
    message: string;
    planId?: string | null;
    payload?: unknown;
    /** JSON-encoded scenario state before the change. Pass null/undefined when N/A. */
    scenarioBefore?: string | null;
    /** JSON-encoded scenario state after the change. Pass null/undefined when N/A. */
    scenarioAfter?: string | null;
    /** Structured diff IF the code already has one. Never compute this on the fly. */
    scenarioDiff?: string | null;
}

export function recordDevLog(entry: DevLogEntry): void {
    if (!logToDevSqlite) return;
    try {
        fetch('/api/dev-log', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
                entries: [{
                    sourceTime: formatDate(new Date()),
                    source: 'browser',
                    category: entry.category,
                    planId: entry.planId ?? null,
                    level: entry.level ?? 'info',
                    message: entry.message,
                    payloadJson: entry.payload === undefined ? null : JSON.stringify(entry.payload),
                    scenarioBefore: entry.scenarioBefore ?? null,
                    scenarioAfter: entry.scenarioAfter ?? null,
                    scenarioDiff: entry.scenarioDiff ?? null,
                }],
            }),
        }).catch(() => { /* swallow */ });
    } catch { /* swallow */ }
}
